import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/feature/SEOHead';
import FAQHero from './components/FAQHero';
import FAQAccordion from './components/FAQAccordion';
import FAQBia from './components/FAQBia';
import FAQContact from './components/FAQContact';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const faqSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é o Carta do Eu Futuro?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O Carta do Eu Futuro é uma plataforma que permite você escrever cartas para si mesmo no futuro. É uma ferramenta poderosa de autoconhecimento e transformação pessoal, baseada em técnicas de PNL e psicologia positiva.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como funciona o envio das cartas?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Você escreve sua carta hoje e escolhe a data futura em que deseja recebê-la. No dia escolhido, enviamos sua carta por e-mail.',
        },
      },
      {
        '@type': 'Question',
        name: 'Minhas cartas são privadas?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim! Suas cartas são completamente privadas e criptografadas. Apenas você tem acesso ao conteúdo.',
        },
      },
      {
        '@type': 'Question',
        name: 'Posso cancelar minha assinatura a qualquer momento?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim, você pode cancelar sua assinatura a qualquer momento sem custos adicionais. Suas cartas já programadas continuarão sendo enviadas normalmente até o fim do período pago.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como funciona o Caderno de Gratidão?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O Caderno de Gratidão é uma ferramenta diária onde você registra momentos de gratidão. Você recebe lembretes diários e pode acompanhar seu progresso.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quais exercícios de PNL estão disponíveis?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Oferecemos diversos exercícios de PNL incluindo Técnica Swish, Ancoragem Emocional, Ressignificação, Linha do Tempo, Modelagem de Excelência e muito mais. Todos com áudios guiados em português.',
        },
      },
      {
        '@type': 'Question',
        name: 'Existe garantia de reembolso?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sim! Oferecemos garantia incondicional de 7 dias. Se não ficar satisfeito, devolvemos 100% do seu investimento sem perguntas.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Perguntas Frequentes (FAQ) | Carta do Eu Futuro',
    url: `${SITE_URL}/faq`,
    inLanguage: 'pt-BR',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${SITE_URL}/faq` },
      ],
    },
  },
];

const FAQ = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-luxury-ivory">
      <SEOHead
        title="Perguntas Frequentes (FAQ) | Carta do Eu Futuro"
        description="Tire suas dúvidas sobre o Carta do Eu Futuro. Como funciona, privacidade das cartas, planos, caderno de gratidão e muito mais."
        keywords="FAQ, perguntas frequentes, carta do eu futuro, dúvidas, como funciona"
        canonical="/faq"
        ogImage="https://public.readdy.ai/ai/img_res/bbebdbbd8f6654c947df17433096ecb5.jpg"
        ogType="website"
        schema={faqSchema}
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 w-full z-50 bg-luxury-black/90 backdrop-blur-md border-b border-gold-500/10">
        <div className="container mx-auto px-6 lg:px-16 flex items-center justify-between h-24">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/9b313db6312c9605d9f9e3f145d3e29c/a68d70c6b66064870b0e9282c113321b.jpeg"
              alt="Carta do Eu Futuro"
              className="w-[72px] h-[72px] object-contain group-hover:scale-110 group-hover:drop-shadow-[0_0_14px_rgba(201,164,64,0.55)] transition-all duration-300"
            />
            <span className="font-['Playfair_Display'] text-lg font-semibold text-white">
              Carta do Eu Futuro
            </span>
          </Link>

          <div className="flex items-center gap-4 sm:gap-8">
            <Link
              to="/"
              className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase hidden sm:block"
            >
              Início
            </Link>
            <Link
              to="/dashboard"
              className="font-['Montserrat'] text-xs text-white/50 hover:text-gold-400 transition-colors cursor-pointer tracking-wider uppercase hidden sm:block"
            >
              Minhas Cartas
            </Link>
            <Link
              to="/faq"
              className="font-['Montserrat'] text-xs text-gold-400 cursor-pointer tracking-wider uppercase hidden sm:block"
            >
              FAQ
            </Link>
            <button
              onClick={() => {
                const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                btn?.click();
              }}
              className="hidden sm:flex items-center gap-2 border border-gold-500/40 text-gold-400 hover:bg-gold-500/10 px-4 py-2 rounded-lg transition-all font-['Montserrat'] text-xs tracking-wider uppercase cursor-pointer whitespace-nowrap"
            >
              <i className="ri-customer-service-2-line text-sm" />
              Falar com a Bia
            </button>
          </div>
        </div>
      </nav>

      <FAQHero />
      <FAQAccordion />
      <FAQBia />
      <FAQContact />
      <Footer />
    </div>
  );
};

export default FAQ;
